import { useAuth } from '../../../context/AuthContext';
import { HOTEL_BASE } from '../model';
import { useProperties } from '../queries';

/**
 * Restaurant or Hotel: which experience a user may enter (IA §3), and the
 * owner-scoped memory of where they last were. Both shells read this; it
 * replaces `showHotel` / `useHotelEntryVisible`.
 *
 * Detection uses only data the app already loads. Who counts as what follows
 * the IA's Q1 recommendation (c) for v1:
 *   - a platform admin has both;
 *   - a user whose own store group lists a property is hotel-only, even when
 *     a hotel outlet gives them a `store_id` (rule (b), telling a restaurant
 *     from a hotel outlet, waits on a backend signal);
 *   - otherwise a user with a store is restaurant-only.
 */
export type Experience = 'restaurant' | 'hotel';

export interface Experiences {
  hasRestaurant: boolean;
  hasHotel: boolean;
  /** user-info or the property list has not answered yet. */
  pending: boolean;
}

/** `<userId>:restaurant|hotel`, the experience the user was last in. */
export const EXPERIENCE_KEY = 'av2.experience';
/** `<userId>:<path>`, the last `/hotel/properties/…` page the user saw. */
export const HOTEL_PATH_KEY = 'av2.experience.hotelPath';

const NONE: Experiences = {
  hasRestaurant: false,
  hasHotel: false,
  pending: false,
};
const PENDING: Experiences = {
  hasRestaurant: false,
  hasHotel: false,
  pending: true,
};
const BOTH: Experiences = {
  hasRestaurant: true,
  hasHotel: true,
  pending: false,
};

export function useExperiences(): Experiences {
  const { user, userInfoSettled } = useAuth();
  const admin = user?.is_platform_admin === true;
  const own = user?.store_group_id ?? null;
  // Shares the cache entry `HotelApp` reads; an admin is never asked for.
  const properties = useProperties(admin ? null : own);

  if (admin) return BOTH;
  if (!user) return userInfoSettled ? NONE : PENDING;
  const hasStore =
    typeof user.store_id === 'number' ||
    (user.available_stores?.length ?? 0) > 0;
  if (own === null) {
    if (!userInfoSettled) return PENDING;
    return { hasRestaurant: hasStore, hasHotel: false, pending: false };
  }
  if (properties.isPending) return PENDING;
  // A failed list (404 for a restaurant group, a 403, a network error) means
  // no hotel to show, which is the same answer as an empty list.
  const hasHotel = (properties.data?.length ?? 0) > 0;
  return { hasRestaurant: hasStore && !hasHotel, hasHotel, pending: false };
}

/** The experience a URL belongs to: `/hotel/*` is hotel, all else restaurant. */
export function experienceOf(pathname: string): Experience {
  return pathname === HOTEL_BASE || pathname.startsWith(`${HOTEL_BASE}/`)
    ? 'hotel'
    : 'restaurant';
}

/**
 * Hotel paths worth returning to: a property page, never the group picker.
 * Only a path the URL parser leaves as it is: navigating resolves `.` and
 * `..` (also percent-encoded, also behind a tab or newline, which it strips)
 * and treats `\` as `/`, so any other path can end up anywhere.
 */
export function isHotelPropertyPath(path: string): boolean {
  if (!path.startsWith(`${HOTEL_BASE}/properties/`)) return false;
  try {
    return new URL(path, 'http://hotel.invalid').pathname === path;
  } catch {
    return false;
  }
}

/* ---- owner-scoped storage, with an in-memory fallback (Safari private) ---- */

const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function write(key: string, value: string | null) {
  if (value === null) memory.delete(key);
  else memory.set(key, value);
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* the value lasts for this page load */
  }
}

/** The stored value if `userId` owns it; another user's value is nobody's. */
function owned(
  value: string | null,
  userId: number | undefined,
): string | null {
  if (!value || userId === undefined) return null;
  const colon = value.indexOf(':');
  if (colon === -1 || value.slice(0, colon) !== String(userId)) return null;
  return value.slice(colon + 1);
}

/** Each shell records itself on mount; the URL stays the source of truth. */
export function rememberExperience(userId: number, experience: Experience) {
  write(EXPERIENCE_KEY, `${userId}:${experience}`);
}

/**
 * The experience `userId` was last in, for choosing a landing page when the
 * URL does not say (`/` after sign-in, IA Q2 (b)). Never overrides a URL.
 */
export function lastExperience(userId: number | undefined): Experience | null {
  const value = owned(read(EXPERIENCE_KEY), userId);
  return value === 'hotel' || value === 'restaurant' ? value : null;
}

/** The hotel shell records property pages so switching back lands there. */
export function rememberHotelPath(userId: number, pathname: string) {
  if (!isHotelPropertyPath(pathname)) return;
  write(HOTEL_PATH_KEY, `${userId}:${pathname}`);
}

/** A group change makes the remembered property page someone else's. */
export function forgetHotelPath(userId: number) {
  if (owned(read(HOTEL_PATH_KEY), userId) !== null) write(HOTEL_PATH_KEY, null);
}

/** The last property page `userId` saw, if one is remembered. */
export function lastHotelPath(userId: number | undefined): string | null {
  const last = owned(read(HOTEL_PATH_KEY), userId);
  return last && isHotelPropertyPath(last) ? last : null;
}

/** Where selecting `target` goes: its last-visited page, else its landing. */
export function experienceLanding(
  target: Experience,
  userId: number | undefined,
): string {
  if (target === 'restaurant') return '/';
  return lastHotelPath(userId) ?? HOTEL_BASE;
}

/**
 * The first protected location a user reaches in this page load (after a
 * reload, or after signing in). Only that one may be redirected to the last
 * experience (IA Q2 (b)); every later `/`, such as the switcher's way back to
 * the restaurant, is an explicit URL and stays put. Leaving that location
 * spends it, so browser Back to it is not a landing either.
 */
let landing: { userId: number; locationKey: string; spent: boolean } | null =
  null;

/**
 * True when `locationKey` is `userId`'s first location this page load and
 * they have not left it yet. Idempotent for re-renders of the same location;
 * a different user (sign out, sign in as someone else) gets a landing of
 * their own.
 */
export function isLanding(userId: number, locationKey: string): boolean {
  if (landing === null || landing.userId !== userId) {
    landing = { userId, locationKey, spent: false };
  } else if (landing.locationKey !== locationKey) {
    landing.spent = true;
  }
  return !landing.spent;
}

/** Test seam: forgets both keys for every user, and the landing. */
export function forgetExperiences() {
  write(EXPERIENCE_KEY, null);
  write(HOTEL_PATH_KEY, null);
  landing = null;
}
